import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { registryKeys } from '../queryKeys';
import { intelClaimsClient } from './intelClaimsClient';
import { AudienceConflictError } from './intelClaimTypes';
// REG-02: RegistryRecordService (pangaea via sabaody).
const client = intelClaimsClient;
export { registryKeys };
/** "{tenant}:{user}" — the identity a cached, viewer-filtered result belongs to. */ function useViewerKey() {
    const { user } = useAuth();
    return `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
}
const errorText = (err, fallback)=>err instanceof Error && err.message ? err.message : fallback;
export function useIntelClaims(subject) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: registryKeys.intelClaims(viewer, subject),
        queryFn: ()=>client.listIntelClaims(subject),
        enabled: !!subject.kind && !!subject.id
    });
}
/**
 * Claims linked to any of `links` (OR-ed), filtered for the viewer by REG
 * (ceiling + audience). GRW-20: a pursuit and its merged stubs, or the
 * competitors' companies. Cached under the intel-claims root, so audience
 * changes patch it like every other claim list.
 */ export function useIntelClaimsForLinks(links, enabled = true) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: registryKeys.intelClaimsForLinks(viewer, links),
        queryFn: ()=>client.listIntelClaimsForLinks(links),
        enabled: enabled && links.length > 0
    });
}
export function useCreateIntelClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input)=>client.createIntelClaim(input),
        onSuccess: ()=>{
            void qc.invalidateQueries({
                queryKey: registryKeys.all
            });
            toast.success('Hearsay saved');
        },
        onError: (err)=>toast.error(errorText(err, 'Could not save hearsay'))
    });
}
export function useRetractIntelClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input)=>client.retractIntelClaim(input),
        onSuccess: ()=>{
            void qc.invalidateQueries({
                queryKey: registryKeys.all
            });
            toast.success('Claim retracted');
        },
        onError: (err)=>toast.error(errorText(err, 'Could not retract the claim'))
    });
}
export function useVerifyIntelClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input)=>client.updateIntelClaimVerification(input),
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: registryKeys.all
            }),
        onError: (err)=>toast.error(errorText(err, 'Could not update the claim'))
    });
}
/** Replace one claim inside every cached claims list (optimistic chip update). */ function patchClaim(qc, claimId, patch) {
    qc.setQueriesData({
        queryKey: [
            ...registryKeys.all,
            'intel-claims'
        ]
    }, (prev)=>prev ? {
            ...prev,
            claims: prev.claims.map((c)=>c.id === claimId ? patch(c) : c)
        } : prev);
}
/**
 * Who can see a claim. Optimistic chip update; when someone changed the
 * audience first (Aborted), refetch and say so.
 */ export function useSetIntelClaimAudience(onChanged) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ claim, audience })=>client.setIntelClaimAudience({
                claimId: claim.id,
                audience,
                expectedAudienceId: claim.audience.audienceId
            }),
        onMutate: ({ claim, audience, optimisticSummary })=>{
            patchClaim(qc, claim.id, (c)=>({
                    ...c,
                    audience: {
                        ...c.audience,
                        level: audience.everyone ? 'EVERYONE' : audience.targets.length === 0 ? 'PERSONAL' : audience.targets.some((t)=>t.kind === 'USER') ? 'PEOPLE' : 'TEAM',
                        summary: optimisticSummary,
                        targets: audience.targets
                    }
                }));
        },
        onSuccess: (saved)=>{
            patchClaim(qc, saved.id, ()=>saved);
            void qc.invalidateQueries({
                queryKey: registryKeys.audienceHistoryAll
            });
            onChanged?.(saved);
        },
        onError: (err)=>{
            void qc.invalidateQueries({
                queryKey: [
                    ...registryKeys.all,
                    'intel-claims'
                ]
            });
            if (err instanceof AudienceConflictError) {
                toast.error('Someone changed this first — check who can see it now.');
            } else {
                toast.error(errorText(err, 'Could not change who can see this'));
            }
        }
    });
}
export function useIntelClaimAudienceHistory(claimId, enabled) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: registryKeys.audienceHistory(viewer, claimId),
        queryFn: ()=>client.listIntelClaimAudienceHistory(claimId),
        enabled: enabled && !!claimId
    });
}
